/* GENERATED FILE — DO NOT EDIT. Source: shared/assets/recently-viewed.js */
import { Component } from '@theme/component';

/**
 * Recently viewed products.
 *
 * Storage note: Horizon ships `RecentlyViewed` in recently-viewed-products.js,
 * but it hard-caps the list at 4 entries via a private static field and exists
 * to feed predictive search. A carousel wants more than 4, and that cap cannot
 * be overridden from outside the class, so this component keeps its own list
 * under a separate key.
 *
 * Rendering uses the Section Rendering API against a product card section, so
 * prices, badges and translations stay server-rendered.
 *
 * @extends {Component}
 */
class TiraRecentlyViewed extends Component {
  static STORAGE_KEY = 'tira:recentlyViewed';

  connectedCallback() {
    super.connectedCallback();

    const currentHandle = this.dataset.currentProduct;
    if (currentHandle) TiraRecentlyViewed.record(currentHandle);

    this.#render();
  }

  /**
   * Adds a product handle to the front of the list.
   * @param {string} handle
   * @param {number} [max]
   */
  static record(handle, max = 12) {
    try {
      const list = TiraRecentlyViewed.read().filter((item) => item !== handle);
      list.unshift(handle);
      localStorage.setItem(TiraRecentlyViewed.STORAGE_KEY, JSON.stringify(list.slice(0, max)));
    } catch {
      // Private browsing or a full quota — recently viewed is non-essential.
    }
  }

  /** @returns {string[]} */
  static read() {
    try {
      const raw = localStorage.getItem(TiraRecentlyViewed.STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter((item) => typeof item === 'string') : [];
    } catch {
      return [];
    }
  }

  async #render() {
    const currentHandle = this.dataset.currentProduct;
    const limit = Number(this.dataset.limit ?? 8);
    const sectionId = this.dataset.sectionId;
    const target = this.querySelector('[data-recently-viewed-list]');

    if (!sectionId || !target) return;

    const handles = TiraRecentlyViewed.read()
      .filter((handle) => handle !== currentHandle)
      .slice(0, limit);

    if (handles.length === 0) {
      this.hidden = true;
      return;
    }

    const root = window.Shopify?.routes?.root ?? '/';

    const cards = await Promise.all(
      handles.map(async (handle) => {
        try {
          const response = await fetch(`${root}products/${handle}?section_id=${sectionId}`);
          if (!response.ok) return null;
          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          // The section wrapper carries Shopify's own id; take its contents only.
          return doc.querySelector('.shopify-section')?.innerHTML ?? null;
        } catch {
          return null;
        }
      }),
    );

    const rendered = cards.filter((card) => card !== null);

    if (rendered.length === 0) {
      this.hidden = true;
      return;
    }

    target.innerHTML = rendered.map((card) => `<li class="tira-recently-viewed__item">${card}</li>`).join('');
    this.hidden = false;
  }
}

if (!customElements.get('tira-recently-viewed')) {
  customElements.define('tira-recently-viewed', TiraRecentlyViewed);
}
